import type { ReactNode } from "react";
import { cn } from "@/utils/cn";

type Tone = "info" | "success" | "error" | "warning";

const tones: Record<Tone, { wrap: string; icon: string }> = {
  info: { wrap: "bg-background-100 border-background-300 text-foreground-800", icon: "ri-information-line text-secondary-600" },
  success: { wrap: "bg-secondary-50 border-secondary-200 text-secondary-900", icon: "ri-checkbox-circle-line text-secondary-600" },
  error: { wrap: "bg-primary-50 border-primary-200 text-primary-900", icon: "ri-error-warning-line text-primary-600" },
  warning: { wrap: "bg-accent-50 border-accent-300 text-accent-950", icon: "ri-alert-line text-accent-600" },
};

interface AlertProps {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
  onRetry?: () => void;
  className?: string;
}

export default function Alert({ tone = "info", title, children, onRetry, className }: AlertProps) {
  return (
    <div className={cn("flex items-start gap-3 rounded-lg border px-4 py-3.5", tones[tone].wrap, className)} role="alert">
      <i className={cn(tones[tone].icon, "text-lg mt-0.5")} aria-hidden="true" />
      <div className="flex-1 text-sm">
        {title && <p className="font-label font-semibold">{title}</p>}
        {children && <div className={cn(title && "mt-1", "leading-relaxed")}>{children}</div>}
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 rounded-md border border-current/30 px-3 py-1.5 text-xs font-label font-medium hover:bg-background-50/60 cursor-pointer"
        >
          Réessayer
        </button>
      )}
    </div>
  );
}